import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { aliasesApi } from '@/features/aliases/api';
import { apiErrorMessage } from '@/api/errors';
import type { Alias, AliasListParams } from '@/features/aliases/types';
import type { AliasFormValues } from '@/features/aliases/schema';

const keys = { all: ['aliases'] as const, list: (p: AliasListParams) => ['aliases', 'list', p] as const };
export const useAliasesList = (p: AliasListParams) =>
  useQuery({ queryKey: keys.list(p), queryFn: () => aliasesApi.list(p), placeholderData: (x) => x });

export function useCreateAlias() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: AliasFormValues) => aliasesApi.create(b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Alias created'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not create alias')) });
}
export function useUpdateAlias() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<AliasFormValues> }) => aliasesApi.update(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not update alias')) });
}
export function useDeleteAlias() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => aliasesApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Alias deleted'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not delete alias')) });
}
export type { Alias };
